import {Injectable} from '@angular/core';
import {HttpClient} from '@angular/common/http';

import {ParametersService} from "./parameters.service";
import {
    ContactForm,
    CountryData,
    ModulesCountries,
    TimeZone,
    ModuleData,
    ModuleEditableData, ContactFormData,
} from "../models";
import {ZipCodePipe} from "../shared";
import {UserService} from "./user.service";
import {AppAlertService} from "./app-alert.service";
import {SubtitlesService} from "./subtitles.service";

@Injectable({
    providedIn: 'root'
})
export class FormsService {
    allCountries: CountryData[] = [];
    captcha: string = '';
    public captcha_key: string = '6Lew43MjAAAAAOvbgXPOW_vZqzotTrGNaU4Vljux';
    contactForm: ContactForm = {
        city: '',
        street: '',
        houseNumber: '',
        phone: '',
        installerInformation: '',
    }
    hasContactData: boolean = false;
    showCountry: boolean = false;
    showModuleCountry: ModulesCountries[] = [];
    timeZones: TimeZone[] | undefined = undefined;
    validCaptcha: boolean | null = null;
    validCity: boolean | null = null;
    validHouseNumber: boolean | null = null;
    validInstallerInformation: boolean | null = null;
    validPhone: boolean | null = null;
    validStreet: boolean | null = null;

    constructor(
        private appAlert: AppAlertService,
        private http: HttpClient,
        private parameters: ParametersService,
        private subtitles: SubtitlesService,
        private user: UserService,
        private zipCodePipe: ZipCodePipe,
    ) {
    }

    validateEmail(email: string): boolean {
        const emailRegex = /^[a-zA-Z0-9._+-]+@[a-zA-Z0-9.-]+\.[a-zA-Z]{2,10}$/;
        return emailRegex.test(email);
    }

    getDefaultCountry(): CountryData | undefined {
        if (Array.isArray(this.allCountries) && this.allCountries.length) {
            return this.allCountries.find(country => country.initial === 'pl');
        }
    }

    getCountryInitial(): void {
        if (this.showModuleCountry.length !== 0) return;

        this.http.get<CountryData[]>('/api/v1/countries_list').subscribe((response ) => {
            this.allCountries = response;

            if (this.parameters.modules.length) {
                this.parameters.modules.forEach((module: ModuleData) => {
                    this.allCountries.forEach((val: CountryData) => {
                        if (val.name === module.country) {
                            this.showModuleCountry[module.id] = {
                                country: val,
                                show: false,
                            };
                        }
                    });
                    if (this.showModuleCountry[module.id] === undefined
                        || this.showModuleCountry[module.id].country === undefined) {
                        this.showModuleCountry[module.id] = {
                            country: this.getDefaultCountry(),
                            show: false,
                        };
                    }
                });
            }
        });
    }

    checkModuleData(module: ModuleData, typeField: keyof ModuleEditableData): boolean {
        if (['name', 'email', 'phoneNumber', 'zipCode', 'additionalInformation', 'country', 'gmtId'].includes(typeField)) {
            const regex = {
                name: /^(.{2,40})$/,
                email: /^\w+([\.-]?\w+)*@\w+([\.-]?\w+)*(\.\w{2,15})+$/,
                phoneNumber: /^([0-9]{9})$/,
                zipCode: module.country === 'Polska' ? /^\d{2}(?:[-])\d{3}$/ : /^([a-zA-Z0-9.-_\s]{3,9})$/,
                additionalInformation: /^(.{2,40})$/
            };

            if (module[typeField] !== null && module[typeField] !== undefined) {
                if (typeField === 'zipCode' && module.country === 'Polska') {
                    module[typeField] = this.zipCodePipe.transform(module[typeField]);
                }
                if ((typeField === 'email' || typeField === 'phoneNumber' || typeField === 'additionalInformation')
                    && (module[typeField] !== undefined && module[typeField].length === 0)) {
                    return true;
                }
                if (typeField === 'country' || typeField === 'gmtId') {
                    return true;
                }
                return regex[typeField].test(module[typeField]);
            }
            return true;
        }
        return true;
    }

    toggleShowCountry(id?: number): void {
        if (id === undefined) {
            this.showCountry = !this.showCountry;
        } else {
            this.showModuleCountry[id].show = !this.showModuleCountry[id].show;
        }
    }

    getTimeZones(): void {
        if (Array.isArray(this.timeZones)) return;

        this.http.get<TimeZone[]>('/api/v1/time_zones').subscribe((response) => {
            this.timeZones = response;
        });
    }

    getGmtTime(module: ModuleData): number {
        let hour = 0;
        let min = 0;

        if (this.timeZones) {
            this.timeZones.forEach((item: TimeZone) => {
                if (module.gmtId === item.id) {
                    hour = parseInt(item.gmt.split(':')[0]);
                    min = parseInt(item.gmt.split(':')[1]) === 0 ? 0 : 0.5;
                }
            });
        }

        return hour + min;
    }

    validateCaptcha(): void {
        this.validCaptcha = this.captcha.length > 0;
    }

    getContactFormData(): void {
        this.http.get<ContactFormData>(`/api/v1/users/${this.user.user_id}/contact_info`).subscribe({
            next: (response) => {
                if (response.code === 0) {
                    this.hasContactData = false;
                } else {
                    this.contactForm = response.data;
                    this.hasContactData = true;
                }
            },
            error: () => {
                this.appAlert.showAppAlert(this.subtitles.setSubtitle(1941), this.subtitles.setSubtitle(1136), null, false, null);
            }
        });
    }

    validateCity(): void {
        let city = this.contactForm.city ?? null;
        this.validCity = !!city && city.length > 0 && city.length <= 45;
    }

    validateStreet(): void {
        let street = this.contactForm.street ?? null;
        this.validStreet = !!street && street.length > 0 && street.length <= 45;
    }

    validateHouseNumber(): void {
        let houseNumber = this.contactForm.houseNumber ?? null;
        this.validHouseNumber = !!houseNumber && houseNumber.length > 0 && houseNumber.length <= 16;
    }

    validatePhone(): void {
        let phone = this.contactForm.phone;
        this.validPhone = phone !== undefined && phone.length > 0 && phone.length <= 45;
    }

    validateInstallerInformation(): void {
        let installerInformation = this.contactForm.installerInformation;
        this.validInstallerInformation = installerInformation.length <= 255;
    }

    isAdditionalInformationValid(): boolean {
        let res = false;

        if (this.validCity !== null ||
            this.validStreet !== null ||
            this.validHouseNumber !== null ||
            this.validPhone !== null) {

            res = ((this.validCity == null || this.validCity) &&
                (this.validStreet == null || this.validStreet) &&
                (this.validHouseNumber == null || this.validHouseNumber) &&
                (this.validInstallerInformation == null || this.validInstallerInformation) &&
                (this.validPhone == null || this.validPhone))
        }

        return res;
    }

    changeContactData(action: 'add' | ''): void {
        let validationState = this.validateContactForm();
        let errorsNumber = validationState.errorsNumber;
        let message = validationState.message;

        if (errorsNumber > 0) {
            this.appAlert.showAppAlert(message, this.subtitles.setSubtitle(1136), null, false, null);
        } else {
            const url = `/api/v1/users/${this.user.user_id}/contact`;
            const method = action === 'add' ? 'POST' : 'PUT';
            const body = this.contactForm;

            this.http.request(method, url, {body}).subscribe({
                next: () => {
                    this.appAlert.showAppAlert(this.subtitles.setSubtitle(2973), this.subtitles.setSubtitle(475), null, false, null);
                    this.hasContactData = true;
                    this.validCity = null;
                    this.validStreet = null;
                    this.validHouseNumber = null;
                    this.validInstallerInformation = null;
                    this.validPhone = null;
                },
                error: () => {
                    this.appAlert.showAppAlert(this.subtitles.setSubtitle(5354), this.subtitles.setSubtitle(1136), null, false, null);
                }
            });
        }
    }

    public validateContactForm(): { errorsNumber: number, message: string } {
        this.validateCity();
        this.validateStreet();
        this.validateHouseNumber();
        this.validatePhone();
        this.validateInstallerInformation();
        let errorsNumber = 0;

        let message = '<div class="text-align-left font-13">';
        if (!this.validCity) {
            errorsNumber += 1;
            message += `${errorsNumber}. ${this.subtitles.setSubtitle(7165)}<br>`; // Nieprawidłowa nazwa miejscowości
        }
        if (!this.validStreet) {
            errorsNumber += 1;
            message += `${errorsNumber}. ${this.subtitles.setSubtitle(7166)}<br>`; // Nieprawidłowa nazwa ulicy
        }
        if (!this.validHouseNumber) {
            errorsNumber += 1;
            message += `${errorsNumber}. ${this.subtitles.setSubtitle(448)}<br>`; // Nieprawidłowy adres
        }
        if (!this.validPhone) {
            errorsNumber += 1;
            message += `${errorsNumber}. ${this.subtitles.setSubtitle(3385)}<br>`; // Nieprawidłowy numer telefonu
        }
        if (!this.validInstallerInformation) {
            errorsNumber += 1;
            message += `${errorsNumber}. ${this.subtitles.setSubtitle(5354)}<br>`; // Nieprawidłowe dane
        }
        return {
            errorsNumber: errorsNumber,
            message: message,
        }
    }

    isCompanySubdomain(company: string): boolean {
        return window.location.hostname.includes(company);
    };

    submitRegistration(data: any) {
        return this.http.post('/api/v1/account', data);
    }
}
